import React from 'react';
import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiAuthError, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select, IconPencil, IconLock } from '../atoms';
import { Checkbox } from '../atoms'; // Annual info-request campaign — enabled toggle
import { StatCard, useToast, NotificationMatrixEditor, StageTemplateEditor, ReductionTypeEditor, LastUpdated, Modal } from '../organisms';
import { COMMON_TIMEZONES } from '../lib/orgTimezone';
import { formatTagLabel } from '../lib/formatTag';
import { latestAttr, type AttrMap } from '../lib/inlineAttribution';

/**
 * OrgSettings — /v2/org-settings
 *
 * The org configuration control plane. Registries backed by live endpoints —
 * document types, contact types, and contact roles — are editable here
 * (super_admin only, enforced server-side). Contact Types (the WHAT a contact
 * is) and Contact Roles (the WHY it's attached) are two parallel registries that
 * both drive the add-contact form. White-label branding (display name, brand colors,
 * outbound-email identity, and the org logo) is a live editor wired to
 * GET/PUT /api/branding + POST /api/branding/logo (admin tier, enforced
 * server-side); the logo bytes are stored in R2/S3 and served back through the
 * org-scoped /api/branding/logo route.
 *
 * Registry writes go through PUT /api/org-settings/{document-types,contact-types,contact-roles}
 * with bodies { document_types: [...] } / { contact_types: [...] } / { contact_roles: [...] }.
 *
 * Domain 03.4 — RBAC, Identity & Org Settings.
 */

interface CurrentUser {
  id: string;
  role: string;
  org_name: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
  logo_url: string | null;
}

interface Org {
  id: string;
  name: string;
  current_system_year: number | null;
  country: string | null;
  status: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
}

export function OrgSettings() {
  const userQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  const orgQuery = useQuery<Org[]>({
    queryKey: ['organizations'],
    queryFn: ({ signal }) => api.get<Org[]>('/api/organizations', signal),
  });

  const contactRolesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-roles'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-roles', signal),
  });

  const contactTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contact-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contact-types', signal),
  });

  const documentTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'document-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/document-types', signal),
  });

  const clientTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'client-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/client-types', signal),
  });
  const clientCategoriesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'client-categories'],
    queryFn: ({ signal }) => api.get('/api/org-settings/client-categories', signal),
  });
  const contractTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'contract-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/contract-types', signal),
  });
  const propertyTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'property-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/property-types', signal),
  });
  // 205 (ATA part 3 §9): the calendar's event types, the firm's own list.
  const eventTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'calendar-event-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/calendar-event-types', signal),
  });
  const unitTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'unit-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/unit-types', signal),
  });
  const propertyClassificationsQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'property-classifications'],
    queryFn: ({ signal }) => api.get('/api/org-settings/property-classifications', signal),
  });
  const taxReturnStatusesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'tax-return-statuses'],
    queryFn: ({ signal }) => api.get('/api/org-settings/tax-return-statuses', signal),
  });
  // Government reference taxonomies (items 1.1 + 1.2) — two SEPARATE registries,
  // each its own query keyed by its slug. They must never be merged.
  const collectorTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'collector-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/collector-types', signal),
  });
  const jurisdictionTypesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'jurisdiction-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/jurisdiction-types', signal),
  });
  // Default groups — admin-defined groups the system materializes + protects. Its
  // own slug; saving it (PUT /api/org-settings/default-groups) creates/reconciles
  // real `groups` rows server-side. May legitimately be empty.
  const defaultGroupsQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'default-groups'],
    queryFn: ({ signal }) => api.get('/api/org-settings/default-groups', signal),
  });

  if (userQuery.isLoading || orgQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  }

  // Role gate
  if (userQuery.data && userQuery.data.role !== 'super_admin' && userQuery.data.role !== 'admin') {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>Org Settings are restricted to super_admin and admin roles.</p>
      </div>
    );
  }

  const org = orgQuery.data?.[0];
  if (!org) return <p style={{ color: 'var(--color-ink-3)' }}>No organization on file.</p>;

  // Registry writes are super_admin only (the backend enforces this too); admins
  // see the registries read-only.
  const canEditRegistries = userQuery.data?.role === 'super_admin';
  const contactRoles = normalizeValues(contactRolesQuery.data);
  const contactTypes = normalizeValues(contactTypesQuery.data);
  const documentTypes = normalizeValues(documentTypesQuery.data);
  const clientTypes = normalizeValues(clientTypesQuery.data);
  const clientCategories = normalizeValues(clientCategoriesQuery.data);
  const contractTypes = normalizeValues(contractTypesQuery.data);
  const propertyTypes = normalizeValues(propertyTypesQuery.data);
  const eventTypes = normalizeValues(eventTypesQuery.data);
  const unitTypes = normalizeValues(unitTypesQuery.data);
  const propertyClassifications = normalizeValues(propertyClassificationsQuery.data);
  const taxReturnStatuses = normalizeValues(taxReturnStatusesQuery.data);
  const collectorTypes = normalizeValues(collectorTypesQuery.data);
  const jurisdictionTypes = normalizeValues(jurisdictionTypesQuery.data);
  const defaultGroups = normalizeValues(defaultGroupsQuery.data);
  // Protected system values (mirror src/config/org-settings.js systemValues) — the
  // canonical enum keys the backend logic + schema defaults depend on.
  const RE_PP_SYSTEM = ['real_estate', 'personal_property', 'real_and_personal'];
  const RETURN_STATUS_SYSTEM = ['pending', 'in_progress', 'prepared', 'reviewed', 'filed', 'filed_amended', 'complete', 'cancelled'];
  // The money-bearing document types. These are already un-renameable (the rename
  // route refuses them, because the money-blind download gate matches document_type
  // verbatim), but the editor never marked them locked and so still offered Remove —
  // the one way to get them out of the list. Server-side removal is now refused too.
  const DOCUMENT_TYPE_SYSTEM = ['Appeal Packet', 'Rendition', 'Tax Bill', 'Invoice'];

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Org Settings</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Configuration for <strong>{org.name}</strong>. Branding is editable by admins; document types and contact roles are editable{canEditRegistries ? '' : ' by a super_admin'}.
        </p>
      </header>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-6)',
      }}>
        <StatCard label="Tax year" value={org.current_system_year ? String(org.current_system_year) : '—'} caption="current_system_year" accent="hunter" />
        <StatCard label="Country" value={org.country || '—'} caption="locale" accent="info" />
        <StatCard label="Status" value={org.status || 'active'} caption="org row" accent="green" />
        <StatCard label="Contact roles" value={String(contactRoles.length)} caption="registry entries" accent="amber" />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 'var(--sp-4)',
        alignItems: 'start',
      }}>
        <BrandingEditor canEdit />

        <TimezoneEditor canEdit />

        {/* Contacts — the two parallel registries (type = WHAT, role = HOW)
            combined under one section. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Contacts</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The vocabulary for the people and organizations attached to your records. A contact <strong>type</strong> is what the contact is; a contact <strong>role</strong> is how it's attached to a given record. Both feed the dropdowns on every Contacts tab.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)', alignItems: 'start' }}>
            <RegistryEditor
              title="Contact types"
              slug="contact-types"
              bodyField="contact_types"
              values={contactTypes}
              loading={contactTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a contact type"
              description="What a contact IS — e.g. client rep, assessor rep, attorney, vendor. Offered whenever a contact is added to a record."
            />
            <RegistryEditor
              title="Contact roles"
              slug="contact-roles"
              bodyField="contact_roles"
              values={contactRoles}
              loading={contactRolesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a contact role"
              description="How a contact is attached to a specific record — e.g. primary, billing, hearing. Set per link, so one contact can hold different roles on different records."
            />
          </div>
        </div>

        {/* Groups — admin-defined default groups the system creates for the whole
            firm and protects from deletion (spec 0008 follow-up). */}
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Groups</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Default groups the system keeps ready for the whole firm. Each name here becomes a real group (created automatically) that anything can be added to and that can't be accidentally deleted. Remove a name and the group stays with everything already in it — it just loses its protected status. Separate from the ad-hoc groups anyone can create on a record.
          </p>
          <RegistryEditor
            title="Default groups"
            slug="default-groups"
            bodyField="default_groups"
            values={defaultGroups}
            loading={defaultGroupsQuery.isLoading}
            canEdit={canEditRegistries}
            addPlaceholder="Add a default group"
            allowEmpty
            disableRename
          />
        </div>

        {/* 7/28 C8: Document Types is its OWN section (was a bare editor jammed after
            Contacts), with room to spread out. It's the single master list every surface
            reads from — internal All Documents, the client portal, and every generator. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Documents</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The single master list of document types used everywhere — the internal All Documents tab, the client portal, and every upload and system-generated document. Locked types (money-bearing artifacts like Tax Bill, Rendition, Appeal Packet) can't be renamed or removed, so their download-redaction gate can't be broken.
          </p>
          <RegistryEditor
            title="Document types"
            slug="document-types"
            bodyField="document_types"
            values={documentTypes}
            loading={documentTypesQuery.isLoading}
            canEdit={canEditRegistries}
            addPlaceholder="Add a document type"
            systemValues={DOCUMENT_TYPE_SYSTEM}
            description="The categories a document can be filed under across the app (e.g. Engagement Letter, Rent Roll, AoA). Drives the Document Type dropdown on every upload."
          />

          {/* Item 2.3 — flag which document types auto-share to the client portal.
              The master list above is split into two buckets; a type auto-shares
              (the client is notified on upload) when it sits in the left bucket. */}
          <div style={{ marginTop: 'var(--sp-4)' }}>
            <DocumentAutoShareEditor
              documentTypes={documentTypes}
              documentTypesLoading={documentTypesQuery.isLoading}
              canEdit={canEditRegistries}
            />
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Clients</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The vocabulary for the client profile Identity and Contract boxes. Renaming a value updates every client already tagged with it.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)', alignItems: 'start' }}>
            <RegistryEditor
              title="Client types"
              slug="client-types"
              bodyField="client_types"
              values={clientTypes}
              loading={clientTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a client type"
              description="The KIND of client — offered as the Type dropdown on the client profile Identity box."
            />
            <RegistryEditor
              title="Client categories"
              slug="client-categories"
              bodyField="client_categories"
              values={clientCategories}
              loading={clientCategoriesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a client category"
              description="The client Category — offered as the Category dropdown on the client profile Identity box."
            />
            <RegistryEditor
              title="Contract types"
              slug="contract-types"
              bodyField="contract_types"
              values={contractTypes}
              loading={contractTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a contract type"
              description="The kind of engagement — offered as the Contract type dropdown on the client profile Contract box."
            />
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Properties</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The vocabulary for the new-property setup flow and the property profile. Renaming a value updates every property already tagged with it.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)', alignItems: 'start' }}>
            <GovernmentDatesEditor canEdit={canEditRegistries} />
            <RegistryEditor
              title="Calendar event types"
              slug="calendar-event-types"
              bodyField="calendar_event_types"
              values={eventTypes}
              loading={eventTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add an event type"
              systemValues={['appeal_deadline', 'hearing', 'rendition_deadline', 'tax_bill', 'reminder']}
              disableRename
              description="The kinds of event the calendar offers. The five built-ins stay; add the rest of your list here or from the event form."
            />
            <RegistryEditor
              title="Property types"
              slug="property-types"
              bodyField="property_types"
              values={propertyTypes}
              loading={propertyTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a property type"
              description="The kind of property — offered as the Property Type dropdown on property setup + the property profile."
            />
            <RegistryEditor
              title="Unit types"
              slug="unit-types"
              bodyField="unit_types"
              values={unitTypes}
              loading={unitTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a unit type"
              description="The unit of measure for a property's units — offered as the Unit Type dropdown on property setup + the property profile."
            />
            <RegistryEditor
              title="Classifications (RE/PP)"
              slug="property-classifications"
              bodyField="property_classifications"
              values={propertyClassifications}
              systemValues={RE_PP_SYSTEM}
              loading={propertyClassificationsQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a classification"
              description="Real-estate vs personal-property — the RE/PP dropdown on property + parcel profiles. The three built-ins are locked (Personal Property drives BPP handling); you can add custom ones."
            />
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Tax returns</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The status vocabulary for the tax-return profile. Renaming a custom value updates every return already tagged with it.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)', alignItems: 'start' }}>
            <RegistryEditor
              title="Return statuses"
              slug="tax-return-statuses"
              bodyField="tax_return_statuses"
              values={taxReturnStatuses}
              systemValues={RETURN_STATUS_SYSTEM}
              loading={taxReturnStatusesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a status"
              description="The lifecycle Status dropdown on the tax-return profile. The 8 built-ins are locked (they key the return lifecycle); custom statuses you add behave as terminal labels."
            />
          </div>
        </div>

        {/* Government (items 1.1 + 1.2) — TWO COMPLETELY SEPARATE registries.
            Collector types and Jurisdiction types are independent lists (their own
            slugs + storageKeys); they are rendered as two distinct cards and must
            never be merged into one. Each surfaces its own "last edited by · when"
            via the shared field-attribution infra. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Government</h3>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            The vocabulary for the government reference profiles. Collector types and jurisdiction types are two separate lists — renaming a value updates every collector or jurisdiction already tagged with it.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)', alignItems: 'start' }}>
            <RegistryEditor
              title="Collector types"
              slug="collector-types"
              bodyField="collector_types"
              values={collectorTypes}
              loading={collectorTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a collector type"
              description="The KIND of tax collector — offered as the Collector Type dropdown on the collector profile."
              attribution={{ entityId: 'collector-types', field: 'collector_types' }}
            />
            <RegistryEditor
              title="Jurisdiction types"
              slug="jurisdiction-types"
              bodyField="jurisdiction_types"
              values={jurisdictionTypes}
              loading={jurisdictionTypesQuery.isLoading}
              canEdit={canEditRegistries}
              addPlaceholder="Add a jurisdiction type"
              description="The KIND of taxing jurisdiction — offered as the Type dropdown on the jurisdiction profile."
              attribution={{ entityId: 'jurisdiction-types', field: 'jurisdiction_types' }}
            />
          </div>
        </div>

        {/* ── Annual info-request campaign ── */}
        <AnnualInfoRequestEditor canEdit={canEditRegistries} />

        {/* ── Notification matrix (feature 41) — full-width grid ── */}
        <NotificationMatrixEditor canEdit={canEditRegistries} />

        {/* ── Appeal lifecycle (respec §2.5 + §2.7) — two structured registries.
            Stage templates lands here as an Org Settings SECTION rather than a
            State-Rules tab: the respec §4.4 says "a tab beside State Rules", but
            State Rules is a standalone page in this app, so per §2.5 ("lives in
            org settings") it's the §2.5 org-settings item. ── */}
        <StageTemplateEditor canEdit={canEditRegistries} />
        <ReductionTypeEditor canEdit={canEditRegistries} />
      </div>

      {/* ── Portal (feature #35) — the client-portal configuration hub ── */}
      <section style={{ marginTop: 'var(--sp-6)' }}>
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--fs-xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>Portal</h2>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>
            What clients can do in the portal — e-signature, document uploads, online payment, and appeal-status visibility. Document types are managed once in the master Document Types list above (7/26 E).
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 'var(--sp-4)',
        }}>
          <PortalSettingsEditor canEdit={canEditRegistries} />
        </div>
      </section>

      {/* ── Email templates (ATA part 4 §4) — the client-facing templates the firm generates ── */}
      <section style={{ marginTop: 'var(--sp-6)' }}>
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--fs-xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>Email templates</h2>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>
            The wording of the emails generated from a client, property or appeal. Merge fields such as {'{{client.name}}'}, {'{{tax_year}}'} and {'{{blocks.signature}}'} fill from appealstack data; a field with no value shows in brackets in the draft so it is filled before sending.
          </p>
        </div>
        <EmailTemplatesEditor canEdit={canEditRegistries} />
        <CommissionRolePercentsEditor canEdit={canEditRegistries} />
        <InvoicingEditor canEdit={canEditRegistries} />
      </section>

      {/* ── Build Mode — the firm's own opt-in, behind accepted terms ── */}
      <section style={{ marginTop: 'var(--sp-6)' }}>
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--fs-xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>Custom Builds</h2>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>
            Request new functionality and watch it move from request to shipped. Switching it on
            requires accepting the Custom Builds terms, and records who accepted them.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 'var(--sp-4)',
        }}>
          <BuildModeEditor canEdit={canEditRegistries} />
        </div>
      </section>
    </>
  );
}

/**
 * BrandingSettings — the shape GET /api/branding resolves (org row merged over
 * platform defaults; every field is always present).
 */
interface BrandingSettings {
  display_name: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  email_from_name: string;
  email_footer: string;
  terminology_overrides: Record<string, string>;
  ai_enabled: boolean;
}

/**
 * BrandingEditor — the live white-label editor. Reads the org's effective
 * branding from GET /api/branding and persists edits via PUT /api/branding
 * (display name, brand colors, outbound-email identity) and the org logo via a
 * multipart POST /api/branding/logo (bytes → R2/S3, served back org-scoped).
 * On any successful write it invalidates the shared ['branding'] query so the
 * operator shell (useBranding) re-themes immediately.
 */
function BrandingEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const brandingQuery = useQuery<BrandingSettings>({
    queryKey: ['branding'],
    queryFn: ({ signal }) => api.get<BrandingSettings>('/api/branding', signal),
    retry: false,
  });

  // Working copy of the editable fields; null until first edit → falls back to
  // the persisted values. A field-level draft keyed by column name.
  type Draft = Pick<BrandingSettings, 'display_name' | 'primary_color' | 'accent_color' | 'email_from_name' | 'email_footer'>;
  const [draft, setDraft] = useState<Partial<Draft>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Cache-buster so the <img> refetches after a new logo is uploaded.
  const [logoVersion, setLogoVersion] = useState(0);

  const persisted = brandingQuery.data;

  if (brandingQuery.isLoading) {
    return (
      <InfoCard label="Branding (white-label)">
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      </InfoCard>
    );
  }
  if (!persisted) {
    return (
      <InfoCard label="Branding (white-label)">
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>Branding unavailable.</p>
      </InfoCard>
    );
  }

  const field = <K extends keyof Draft>(k: K): string => (draft[k] ?? persisted[k]) as string;
  const setField = <K extends keyof Draft>(k: K, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const dirty = (Object.keys(draft) as (keyof Draft)[]).some((k) => draft[k] !== undefined && draft[k] !== persisted[k]);

  async function save() {
    setSaving(true);
    try {
      const body: Partial<Draft> = {};
      for (const k of Object.keys(draft) as (keyof Draft)[]) {
        if (draft[k] !== undefined && draft[k] !== persisted![k]) body[k] = draft[k];
      }
      await api.put('/api/branding', body);
      await queryClient.invalidateQueries({ queryKey: ['branding'] });
      setDraft({});
      toast({ variant: 'success', message: 'Branding saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  async function onLogoPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    try {
      // Multipart upload — the JSON api client can't send FormData, so use fetch
      // directly (same-origin cookies + the correlation header, like api.ts).
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/branding/logo', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'x-request-id': reqId() },
        body: fd,
      });
      if (!res.ok) {
        let msg = `Upload failed (HTTP ${res.status}).`;
        try { const b = await res.json(); if (b && b.error) msg = String(b.error); } catch { /* keep default */ }
        throw new Error(msg);
      }
      await queryClient.invalidateQueries({ queryKey: ['branding'] });
      setLogoVersion((v) => v + 1);
      toast({ variant: 'success', message: 'Logo uploaded.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Logo upload failed.') });
    } finally {
      setUploading(false);
    }
  }

  const hasLogo = !!persisted.logo_url;
  const logoSrc = hasLogo ? `/api/branding/logo?v=${logoVersion}` : null;

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Branding (white-label)</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
        <div style={{
          width: 64, height: 64, flexShrink: 0,
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {logoSrc ? (
            <img src={logoSrc} alt="Org logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>No logo</span>
          )}
        </div>
        {canEdit && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
              onChange={onLogoPicked}
              style={{ display: 'none' }}
            />
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : hasLogo ? 'Replace logo' : 'Upload logo'}
            </Button>
            <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              PNG, JPEG, SVG, WebP or GIF · up to 2MB
            </p>
          </div>
        )}
      </div>

      <FieldRow label="Display name">
        <Input value={field('display_name')} disabled={!canEdit} onChange={(e) => setField('display_name', e.target.value)} />
      </FieldRow>

      <FieldRow label="Primary color">
        <ColorField value={field('primary_color')} disabled={!canEdit} onChange={(v) => setField('primary_color', v)} />
      </FieldRow>

      <FieldRow label="Accent color">
        <ColorField value={field('accent_color')} disabled={!canEdit} onChange={(v) => setField('accent_color', v)} />
      </FieldRow>

      <FieldRow label="Email from-name">
        <Input value={field('email_from_name')} disabled={!canEdit} onChange={(e) => setField('email_from_name', e.target.value)} />
      </FieldRow>

      <FieldRow label="Email footer">
        <textarea
          value={field('email_footer')}
          disabled={!canEdit}
          onChange={(e) => setField('email_footer', e.target.value)}
          rows={2}
          style={{
            fontFamily: 'inherit',
            fontSize: 'var(--fs-md)',
            padding: '8px 12px',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            background: 'var(--color-card)',
            color: 'var(--color-ink)',
            outline: 'none',
            width: '100%',
            resize: 'vertical',
          }}
        />
      </FieldRow>

      <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        Colors apply app-wide via the <code style={{ fontFamily: 'var(--font-mono)' }}>--brand-*</code> tokens; the from-name and footer brand outbound email.
      </p>
    </div>
  );
}

/**
 * TimezoneEditor — the org's IANA timezone (organizations.timezone, a real
 * column). Reads GET /api/org-settings/timezone and persists via PUT (admin tier,
 * enforced server-side). This zone cross-cuts the whole product: the Calendar
 * renders event times and reminders in it, and reminder fire-times are computed
 * against it — so on save we invalidate ['current-user'] (which now carries
 * `timezone`) so the calendar re-themes to the new zone immediately.
 */
function TimezoneEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const tzQuery = useQuery<{ timezone: string }>({
    queryKey: ['org-settings', 'timezone'],
    queryFn: ({ signal }) => api.get<{ timezone: string }>('/api/org-settings/timezone', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const persisted = tzQuery.data?.timezone ?? '';
  const value = draft ?? persisted;
  const dirty = draft !== null && draft !== persisted;

  // Merge any persisted zone that isn't in the curated list so it still shows.
  const options = COMMON_TIMEZONES.some((o) => o.value === persisted) || !persisted
    ? COMMON_TIMEZONES
    : [{ value: persisted, label: persisted }, ...COMMON_TIMEZONES];

  async function save() {
    setSaving(true);
    try {
      await api.put('/api/org-settings/timezone', { timezone: value });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'timezone'] });
      // The calendar reads the org tz off /api/current-user — refresh it so
      // event/reminder rendering picks up the new zone without a reload.
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Timezone saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Organization timezone</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {tzQuery.isLoading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <>
          <Select
            options={options}
            value={value}
            disabled={!canEdit}
            onChange={(e) => setDraft(e.target.value)}
          />
          <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Drives Calendar date/time rendering and reminder fire-times. An 8:00 reminder fires at 08:00 in this zone.
          </p>
        </>
      )}
    </div>
  );
}

/** A labeled control row for the branding editor. */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--sp-3)' }}>
      <label style={{
        display: 'block',
        marginBottom: 'var(--sp-1)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--color-ink-3)',
      }}>{label}</label>
      {children}
    </div>
  );
}

/** A color swatch (native picker) paired with a hex text input, kept in sync. */
function ColorField({ value, disabled, onChange }: { value: string; disabled: boolean; onChange: (v: string) => void }) {
  // The native <input type="color"> needs a valid #rrggbb; fall back for odd values.
  const swatch = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
      <input
        type="color"
        value={swatch}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Color picker"
        style={{
          width: 36, height: 36, padding: 0,
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
          background: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
      <Input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ fontFamily: 'var(--font-mono)' }}
      />
    </div>
  );
}

/** Extract a human message from an ApiClientError body or Error. */
function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body) {
    return String((err.body as { error: string }).error);
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Correlation id for the raw multipart fetch (mirrors lib/api.ts). */
function reqId(): string {
  try { return crypto.randomUUID(); } catch { return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
}

/**
 * humanizeLabel — render a taxonomy value with spaces + title case for DISPLAY
 * (retest B4: "Personal Property", not "Personal_Property"). Only transforms
 * slug-form values (those containing an underscore) — a value already in
 * display-name form is returned untouched so a small word is not wrongly
 * re-capitalized ("Notice of Value" stays "Notice of Value", not "Notice Of
 * Value"). The STORED value is never changed; only the rendered text.
 */
// 7/28 A5: org settings is THE formatting standard — every tag reads first-letter
// capitalized + no underscores, including a lowercase no-underscore value like "attorney"
// (which the old body left lowercase). Delegate to the single shared formatter so org
// settings, the contact badges, and the portal all render tags identically.
function humanizeLabel(v: string): string {
  return formatTagLabel(v);
}

/**
 * RegistryEditor — chip list with add/remove + save for a string-array org
 * setting. Tracks a working copy against the persisted values; Save is enabled
 * only when the set differs. Persists via PUT /api/org-settings/:slug and
 * invalidates the cached query on success.
 */
function RegistryEditor({
  title, slug, bodyField, values, loading, canEdit, addPlaceholder, description, systemValues, attribution, allowEmpty, disableRename,
}: {
  title: string;
  slug: string;
  bodyField: string;
  values: string[];
  loading: boolean;
  canEdit: boolean;
  addPlaceholder: string;
  description?: string;
  /** Protected built-in values — can be reordered but not renamed or removed. */
  systemValues?: string[];
  /** Allow saving an empty list (default false: at least one entry required). Used by
   *  registries whose value legitimately may be empty, e.g. Default groups. */
  allowEmpty?: boolean;
  /** Hide the in-place rename affordance. Used where a value is matched elsewhere by
   *  its exact string (Default groups → group name), so a rename here would desync. */
  disableRename?: boolean;
  /** When set, show a per-registry "last edited by · when" line, read from the
   *  shared field-attribution infra (audit_logs, entity_type='org_settings').
   *  `entityId` is the registry slug; `field` is its storageKey. */
  attribution?: { entityId: string; field: string };
}) {
  const isLocked = (v: string) => !!systemValues && systemValues.includes(v);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  // Last edited (optional) — reuse GET /api/field-attribution/org_settings/:slug
  // + the LastUpdated organism. Disabled when this registry isn't audited, so the
  // other RegistryEditors are unaffected. A read failure (e.g. a non-super_admin
  // with no org_settings permission) resolves to nothing rather than an error box.
  const attrKey = ['field-attribution', 'org_settings', attribution?.entityId];
  const attrQuery = useQuery<AttrMap>({
    queryKey: attrKey,
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/org_settings/${attribution!.entityId}`, signal),
    enabled: !!attribution,
    retry: false,
  });
  const lastEdited = attribution ? latestAttr(attrQuery.data ?? {}, [attribution.field]) : null;

  // The working copy defaults to the persisted values until the user edits.
  const working = draft ?? values;
  const dirty = draft !== null && !sameSet(draft, values);

  function addValue() {
    const v = newValue.trim();
    if (!v) return;
    if (working.some(x => x.toLowerCase() === v.toLowerCase())) {
      toast({ variant: 'error', message: `"${v}" is already in the list.` });
      return;
    }
    setDraft([...working, v]);
    setNewValue('');
  }

  function removeValue(v: string) {
    if (isLocked(v)) return; // built-in — protected from removal
    setDraft(working.filter(x => x !== v));
  }

  // §6 — rename an existing value in place. Unlike add/remove (batched into the
  // draft + Save), a rename is an IMMEDIATE server op: it needs the OLD value to
  // cascade the rename to every record already tagged with it, so no stale
  // labels are left behind. Only offered when there are no pending draft edits.
  async function renameValue(from: string) {
    const to = editText.trim();
    if (!to || to === from) { setEditingValue(null); return; }
    if (working.some(x => x !== from && x.toLowerCase() === to.toLowerCase())) {
      toast({ variant: 'error', message: `"${to}" is already in the list.` });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put<{ updated?: number }>(`/api/org-settings/${slug}/rename`, { from, to });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', slug] });
      if (attribution) await queryClient.invalidateQueries({ queryKey: attrKey });
      setEditingValue(null);
      setDraft(null); // reflect server truth (rename already persisted)
      const n = res && typeof res.updated === 'number' ? res.updated : 0;
      toast({ variant: 'success', message: `Renamed to "${to}"${n ? ` · ${n} record${n === 1 ? '' : 's'} updated` : ''}.` });
    } catch (err) {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Rename failed.';
      toast({ variant: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!allowEmpty && working.length === 0) {
      toast({ variant: 'error', message: 'Keep at least one entry.' });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/org-settings/${slug}`, { [bodyField]: working });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', slug] });
      if (attribution) await queryClient.invalidateQueries({ queryKey: attrKey });
      setDraft(null);
      toast({ variant: 'success', message: `${title} saved.` });
    } catch (err) {
      const msg = err instanceof ApiClientError && typeof err.body === 'object' && err.body && 'error' in err.body
        ? String((err.body as { error: string }).error)
        : 'Save failed.';
      toast({ variant: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>{title} ({working.length})</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {description && (
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal, 1.5)' }}>
          {description}
        </p>
      )}

      {loading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : working.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>None configured.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
          {working.map(v => (
            editingValue === v ? (
              <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
                <Input
                  value={editText}
                  autoFocus
                  aria-label={`Rename ${v}`}
                  onChange={e => setEditText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); renameValue(v); }
                    if (e.key === 'Escape') { e.preventDefault(); setEditingValue(null); }
                  }}
                  style={{ width: 160 }}
                />
                <Button variant="primary" size="sm" onClick={() => renameValue(v)} disabled={saving}>Save</Button>
                <Button variant="secondary" size="sm" onClick={() => setEditingValue(null)} disabled={saving}>Cancel</Button>
              </span>
            ) : (
              <span key={v} style={{ display: 'inline-flex' }}>
                <Badge variant="neutral">
                  {/* B4 — display the label with spaces, never the raw slug
                      ("Personal Property", not "Personal_Property"). The stored
                      value (v) is unchanged; only the rendered text is humanized. */}
                  {humanizeLabel(v)}
                  {/* Built-in system value — a line lock (no emoji, retest B4). */}
                  {isLocked(v) && (
                    <span
                      title="Built-in value — can be reordered but not renamed or removed"
                      aria-label="Locked built-in value"
                      style={{ marginLeft: 'var(--sp-1)', color: 'var(--color-ink-3)', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
                    ><IconLock size={11} /></span>
                  )}
                  {/* §6 — rename in place (edits every tagged record). Hidden while
                      there are unsaved add/remove edits, so the two don't collide. */}
                  {canEdit && !dirty && !isLocked(v) && !disableRename && (
                    <button
                      type="button"
                      onClick={() => { setEditingValue(v); setEditText(v); }}
                      aria-label={`Edit ${v}`}
                      title={`Rename "${v}" (updates every record using it)`}
                      style={{
                        marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                        color: 'var(--color-ink-3)', cursor: 'pointer', lineHeight: 1, padding: 0,
                        display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle',
                      }}
                    ><IconPencil size={11} /></button>
                  )}
                  {canEdit && !editingValue && !isLocked(v) && (
                    <button
                      type="button"
                      onClick={() => removeValue(v)}
                      aria-label={`Remove ${v}`}
                      style={{
                        marginLeft: 'var(--sp-1)',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-ink-3)',
                        cursor: 'pointer',
                        fontSize: 'var(--fs-sm)',
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >×</button>
                  )}
                </Badge>
              </span>
            )
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
          <Input
            value={newValue}
            placeholder={addPlaceholder}
            onChange={e => setNewValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addValue(); } }}
          />
          <Button variant="secondary" size="sm" onClick={addValue} disabled={!newValue.trim()}>
            Add
          </Button>
        </div>
      )}

      {/* Last edited by · when — reused attribution infra (audit_logs +
          LastUpdated), shown only for registries that carry an audited history. */}
      {lastEdited && (lastEdited.by || lastEdited.at) && (
        <div style={{ marginTop: 'var(--sp-3)', textAlign: 'right' }}>
          <LastUpdated updatedBy={lastEdited.by} updatedAt={lastEdited.at} />
        </div>
      )}
    </div>
  );
}

/**
 * DocumentAutoShareEditor — item 2.3. Presents the master document-types list
 * split into two labeled buckets — "Auto-shares to client portal" and "Manual
 * only" — and lets a super_admin move a type between them to toggle its
 * auto-share membership. Auto-share means the type flips notify_client=TRUE on
 * upload (the client is notified); the READ path lives in src/routes/documents.js.
 *
 * The membership set is its OWN live config (GET/PUT /api/document-auto-share-types)
 * — the SAME query key the upload modal reads, so a move here refreshes the
 * modal's "will be shared" hint. The master list stays editable via the sibling
 * RegistryEditor; this only flags which of those types auto-share.
 *
 * Two ways to move a type (both write the same PUT): drag a chip between buckets
 * (HTML5 draggable + onDragStart/onDragOver/onDrop — no external DnD lib, CSP-safe),
 * or use the per-chip "→ move" affordance (a real <button>, so it is keyboard- and
 * screen-reader-operable — the accessible fallback to drag).
 */
function DocumentAutoShareEditor({
  documentTypes, documentTypesLoading, canEdit,
}: {
  documentTypes: string[];
  documentTypesLoading: boolean;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [dragType, setDragType] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<'auto' | 'manual' | null>(null);

  const autoShareQuery = useQuery<string[]>({
    queryKey: ['document-auto-share-types'],
    queryFn: ({ signal }) => api.get<string[]>('/api/document-auto-share-types', signal),
    retry: false,
  });

  const loading = documentTypesLoading || autoShareQuery.isLoading;
  // The live auto-share set. Partition the MASTER list by membership so the two
  // buckets always add up to the master document-types list.
  const autoShareSet = new Set(autoShareQuery.data ?? []);
  const autoTypes = documentTypes.filter((t) => autoShareSet.has(t));
  const manualTypes = documentTypes.filter((t) => !autoShareSet.has(t));

  // Compute the next set from the current one (preserving any stored auto-share
  // entry not in the master list) and PUT it. A no-op move (already in the target
  // bucket) is ignored.
  async function move(type: string, toAutoShare: boolean) {
    if (!canEdit || saving) return;
    if (autoShareSet.has(type) === toAutoShare) return;
    const next = toAutoShare
      ? [...autoShareSet, type]
      : [...autoShareSet].filter((t) => t !== type);
    setSaving(true);
    try {
      await api.put('/api/document-auto-share-types', { document_types: next });
      await queryClient.invalidateQueries({ queryKey: ['document-auto-share-types'] });
      toast({
        variant: 'success',
        message: toAutoShare
          ? `"${type}" now auto-shares to the client portal.`
          : `"${type}" is now manual only.`,
      });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  function onDrop(toAutoShare: boolean) {
    return (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(null);
      // Prefer the dataTransfer payload (survives re-renders); fall back to state.
      const type = e.dataTransfer.getData('text/plain') || dragType;
      setDragType(null);
      if (type) move(type, toAutoShare);
    };
  }

  const bucket = (
    kind: 'auto' | 'manual',
    label: string,
    hint: string,
    types: string[],
  ) => {
    const toAutoShare = kind === 'auto';
    return (
      <div
        role="group"
        onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOver(kind); } : undefined}
        onDragLeave={canEdit ? () => setDragOver((cur) => (cur === kind ? null : cur)) : undefined}
        onDrop={canEdit ? onDrop(toAutoShare) : undefined}
        aria-label={label}
        style={{
          border: `1px ${dragOver === kind ? 'solid' : 'dashed'} ${dragOver === kind ? 'var(--brand-primary)' : 'var(--color-border)'}`,
          background: dragOver === kind ? 'var(--brand-primary-bg)' : 'var(--color-bg)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-3)',
          minHeight: 96,
          transition: 'background 120ms, border-color 120ms',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
          <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{label}</span>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>({types.length})</span>
        </div>
        <p style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{hint}</p>
        {types.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
            {canEdit ? 'Drag a type here, or use the move control.' : 'None.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
            {types.map((t) => (
              <span
                key={t}
                draggable={canEdit && !saving}
                onDragStart={canEdit ? (e) => { e.dataTransfer.setData('text/plain', t); e.dataTransfer.effectAllowed = 'move'; setDragType(t); } : undefined}
                onDragEnd={() => { setDragType(null); setDragOver(null); }}
                style={{ display: 'inline-flex', cursor: canEdit && !saving ? 'grab' : 'default', opacity: dragType === t ? 0.5 : 1 }}
              >
                <Badge variant={toAutoShare ? 'success' : 'neutral'}>
                  {humanizeLabel(t)}
                  {canEdit && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => move(t, !toAutoShare)}
                      aria-label={toAutoShare ? `Move ${t} to manual only` : `Move ${t} to auto-share`}
                      title={toAutoShare ? `Move "${t}" to manual only` : `Move "${t}" to auto-share`}
                      style={{
                        marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                        color: 'var(--color-ink-3)', cursor: saving ? 'default' : 'pointer',
                        fontSize: 'var(--fs-xs)', lineHeight: 1, padding: 0, whiteSpace: 'nowrap',
                      }}
                    >{toAutoShare ? '→ Manual' : '→ Auto-share'}</button>
                  )}
                </Badge>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-1)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>Client-portal auto-share</h3>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal, 1.5)' }}>
        A document type in <strong>Auto-shares to client portal</strong> is sent to the client portal automatically on upload — the client is notified, no extra step. Everything else is <strong>Manual only</strong>. Drag a type between the buckets (or use its move control) to change it.
      </p>

      {loading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : documentTypes.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>Add a document type above first.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 'var(--sp-3)', alignItems: 'stretch' }}>
          {bucket('auto', 'Auto-shares to client portal', 'Sent to the portal + client notified on upload.', autoTypes)}
          {bucket('manual', 'Manual only', 'Stays internal until someone shares it.', manualTypes)}
        </div>
      )}
    </div>
  );
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every(x => bs.has(x));
}

function normalizeValues(data: string[] | { values: string[] } | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.values || [];
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

/**
 * The annual info-request campaign config (GET/PUT /api/org-settings/annual-info-request).
 * A composite object: enabled + lead_time_days + document_types + anchor_date. The
 * campaign (backend) raises one information request per active client each year,
 * `lead_time_days` ahead of `anchor_date`, with `document_types` as the requested
 * items. Frequency is annual (fixed).
 */
interface AnnualInfoRequestConfig {
  enabled: boolean;
  lead_time_days: number;
  document_types: string[];
  anchor_date: string | null;
}

/**
 * AnnualInfoRequestEditor — the Org Settings card for the automated annual
 * information-request campaign. Toggles the campaign on/off, sets how far ahead of
 * the deadline it fires, edits the document-type checklist (the requested items),
 * and sets the yearly deadline anchor (MM-DD). super_admin only (server-enforced).
 */
function AnnualInfoRequestEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const cfgQuery = useQuery<AnnualInfoRequestConfig>({
    queryKey: ['org-settings', 'annual-info-request'],
    queryFn: ({ signal }) => api.get<AnnualInfoRequestConfig>('/api/org-settings/annual-info-request', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<AnnualInfoRequestConfig | null>(null);
  const [newDocType, setNewDocType] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingDoc, setEditingDoc] = useState<string | null>(null);
  const [editDocText, setEditDocText] = useState('');

  const persisted = cfgQuery.data;
  const value = draft ?? persisted ?? null;

  const dirty = draft !== null && persisted != null && (
    draft.enabled !== persisted.enabled ||
    draft.lead_time_days !== persisted.lead_time_days ||
    (draft.anchor_date || '') !== (persisted.anchor_date || '') ||
    !sameSet(draft.document_types, persisted.document_types)
  );

  function edit(patch: Partial<AnnualInfoRequestConfig>) {
    setDraft({ ...(value as AnnualInfoRequestConfig), ...patch });
  }

  function addDocType() {
    const v = newDocType.trim();
    if (!v || !value) return;
    if (value.document_types.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast({ variant: 'error', message: `"${v}" is already in the list.` });
      return;
    }
    edit({ document_types: [...value.document_types, v] });
    setNewDocType('');
  }

  // B4 — rename a document type in place. Like the RegistryEditor rename, this is
  // an IMMEDIATE server op (not batched into the draft) because it must cascade
  // the rename to every already-raised requested item tagged with it. Offered
  // only when there are no pending draft edits, so the two never collide.
  async function renameDocType(from: string) {
    const to = editDocText.trim();
    if (!to || to === from) { setEditingDoc(null); return; }
    if (value && value.document_types.some((x) => x !== from && x.toLowerCase() === to.toLowerCase())) {
      toast({ variant: 'error', message: `"${to}" is already in the list.` });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put<{ updated?: number }>('/api/org-settings/annual-info-request/rename-document-type', { from, to });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'annual-info-request'] });
      setEditingDoc(null);
      setDraft(null); // reflect server truth (rename already persisted)
      const n = res && typeof res.updated === 'number' ? res.updated : 0;
      toast({ variant: 'success', message: `Renamed to "${to}"${n ? ` · ${n} request${n === 1 ? '' : 's'} updated` : ''}.` });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Rename failed.') });
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!value) return;
    if (value.document_types.length === 0) {
      toast({ variant: 'error', message: 'Keep at least one document type.' });
      return;
    }
    if (value.anchor_date && !/^(?:\d{4}-)?\d{2}-\d{2}$/.test(value.anchor_date)) {
      toast({ variant: 'error', message: 'Deadline must be MM-DD (e.g. 04-15).' });
      return;
    }
    setSaving(true);
    try {
      await api.put('/api/org-settings/annual-info-request', {
        annual_info_request: {
          enabled: value.enabled,
          lead_time_days: value.lead_time_days,
          document_types: value.document_types,
          anchor_date: value.anchor_date || null,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'annual-info-request'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Annual information request saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Annual information request</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {cfgQuery.isLoading || !value ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <>
          <div style={{ marginBottom: 'var(--sp-3)' }}>
            <Checkbox
              label="Enable the annual campaign"
              description="Raise one information request per active client each year, ahead of the deadline."
              checked={value.enabled}
              disabled={!canEdit}
              onChange={(e) => edit({ enabled: e.target.checked })}
            />
          </div>

          <FieldRow label="Lead time (days before deadline)">
            <Input
              type="number"
              min={0}
              max={366}
              value={String(value.lead_time_days)}
              disabled={!canEdit}
              onChange={(e) => edit({ lead_time_days: Math.max(0, Math.min(366, Number(e.target.value) || 0)) })}
            />
          </FieldRow>

          <FieldRow label="Deadline (MM-DD)">
            <Input
              placeholder="04-15"
              value={value.anchor_date ?? ''}
              disabled={!canEdit}
              onChange={(e) => edit({ anchor_date: e.target.value.trim() === '' ? null : e.target.value.trim() })}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </FieldRow>

          <label style={{
            display: 'block',
            marginBottom: 'var(--sp-1)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--color-ink-3)',
          }}>Document types requested ({value.document_types.length})</label>
          {value.document_types.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--color-muted)' }}>None configured.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center' }}>
              {value.document_types.map((v) => (
                editingDoc === v ? (
                  <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
                    <Input
                      value={editDocText}
                      autoFocus
                      aria-label={`Rename ${v}`}
                      onChange={(e) => setEditDocText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); renameDocType(v); }
                        if (e.key === 'Escape') { e.preventDefault(); setEditingDoc(null); }
                      }}
                      style={{ width: 200 }}
                    />
                    <Button variant="primary" size="sm" onClick={() => renameDocType(v)} disabled={saving}>Save</Button>
                    <Button variant="secondary" size="sm" onClick={() => setEditingDoc(null)} disabled={saving}>Cancel</Button>
                  </span>
                ) : (
                  <span key={v} style={{ display: 'inline-flex' }}>
                    <Badge variant="neutral">
                      {v}
                      {/* B4 — rename in place (cascades to every tagged request).
                          Hidden while there are unsaved add/remove edits so the
                          immediate rename and the batched draft don't collide. */}
                      {canEdit && !dirty && (
                        <button
                          type="button"
                          onClick={() => { setEditingDoc(v); setEditDocText(v); }}
                          aria-label={`Edit ${v}`}
                          title={`Rename "${v}" (updates every request using it)`}
                          style={{
                            marginLeft: 'var(--sp-1)', border: 'none', background: 'transparent',
                            color: 'var(--color-ink-3)', cursor: 'pointer', lineHeight: 1, padding: 0,
                            display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle',
                          }}
                        ><IconPencil size={11} /></button>
                      )}
                      {canEdit && !editingDoc && (
                        <button
                          type="button"
                          onClick={() => edit({ document_types: value.document_types.filter((x) => x !== v) })}
                          aria-label={`Remove ${v}`}
                          style={{
                            marginLeft: 'var(--sp-1)',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--color-ink-3)',
                            cursor: 'pointer',
                            fontSize: 'var(--fs-sm)',
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >×</button>
                      )}
                    </Badge>
                  </span>
                )
              ))}
            </div>
          )}

          {canEdit && (
            <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
              <Input
                value={newDocType}
                placeholder="Add a document type"
                onChange={(e) => setNewDocType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDocType(); } }}
              />
              <Button variant="secondary" size="sm" onClick={addDocType} disabled={!newDocType.trim()}>
                Add
              </Button>
            </div>
          )}

          <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Frequency is annual. Each request attaches the client's current property list, writes the deadline to the calendar, and notifies the client's portal users.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * BuildMode — the firm's own opt-in, gated behind accepted terms.
 *
 * The toggle is a LEGAL GATE, not a preference. Turning it on requires reading the terms
 * and accepting them in the same action, and the acceptance is recorded against the person
 * who gave it. The server enforces all of that independently (see routes/org-settings.js) —
 * this UI is the humane way to satisfy the gate, never the gate itself.
 *
 * Switching OFF deliberately keeps the acceptance record: consent given is a fact that
 * happened, and erasing it on every toggle would destroy the trail the gate exists to make.
 */
interface BuildModeConfig {
  enabled: boolean;
  accepted_terms_at: string | null;
  accepted_by_user_id: string | null;
  terms_version: string | null;
  terms?: string;
  current_terms_version?: string;
  accepted_current?: boolean;
}

function BuildModeEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showTerms, setShowTerms] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  const cfgQuery = useQuery<BuildModeConfig>({
    queryKey: ['org-settings', 'build-mode'],
    queryFn: ({ signal }) => api.get<BuildModeConfig>('/api/org-settings/build-mode', signal),
    retry: false,
  });
  const cfg = cfgQuery.data;

  async function setEnabled(next: boolean) {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.put('/api/org-settings/build-mode', {
        build_mode: { enabled: next },
        // Only meaningful when turning ON; the server ignores it otherwise.
        ...(next ? { accept_terms_version: cfg.current_terms_version } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'build-mode'] });
      // The nav entry follows this flag, so the flag list has to be refetched too or the
      // Build Mode link would not appear (or disappear) until a full reload.
      await queryClient.invalidateQueries({ queryKey: ['flags'] });
      setShowTerms(false);
      setAgreed(false);
      toast({ variant: 'success', message: next ? 'Custom Builds is on.' : 'Custom Builds is off.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Could not change Custom Builds.') });
    } finally {
      setSaving(false);
    }
  }

  const card: React.CSSProperties = {
    background: 'var(--color-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--r-lg)',
    padding: 'var(--sp-4)',
  };

  if (cfgQuery.isLoading || !cfg) {
    return <div style={card}><p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p></div>;
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Custom Builds</h3>
        <span style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          padding: '2px 10px',
          borderRadius: 999,
          background: cfg.enabled ? 'var(--color-status-green-bg)' : 'var(--color-bg)',
          color: cfg.enabled ? 'var(--color-status-green)' : 'var(--color-ink-3)',
          border: '1px solid var(--color-border)',
        }}>{cfg.enabled ? 'On' : 'Off'}</span>
      </div>

      <p style={{ margin: 'var(--sp-3) 0 0', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)' }}>
        {cfg.enabled
          ? 'Your firm can request builds. Super admins see Custom Builds in the sidebar.'
          : 'Off. Nobody in your firm can request a build until this is switched on.'}
      </p>

      {cfg.accepted_terms_at && (
        <p style={{ margin: 'var(--sp-2) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}>
          Terms accepted {new Date(cfg.accepted_terms_at).toLocaleDateString()}
          {cfg.accepted_current === false && ' — the terms have changed since, so they must be accepted again.'}
        </p>
      )}

      {!canEdit && (
        <p style={{ margin: 'var(--sp-3) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}>
          Only a super admin can change this.
        </p>
      )}

      {canEdit && (
        <div style={{ marginTop: 'var(--sp-4)' }}>
          {cfg.enabled ? (
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => setEnabled(false)}>
              {saving ? 'Working…' : 'Turn off'}
            </Button>
          ) : (
            <Button variant="primary" size="sm" disabled={saving} onClick={() => { setAgreed(false); setShowTerms(true); }}>
              Review terms &amp; turn on
            </Button>
          )}
        </div>
      )}

      <Modal open={showTerms} onClose={() => setShowTerms(false)} title="Custom Builds terms" size="lg">
        <div style={{
          whiteSpace: 'pre-wrap',
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-ink-2)',
          lineHeight: 1.65,
          maxHeight: '46vh',
          overflowY: 'auto',
          padding: 'var(--sp-3)',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
        }}>{cfg.terms}</div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)',
          marginTop: 'var(--sp-4)', fontSize: 'var(--fs-sm)', cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            I have read and accept these terms on behalf of my firm. My name and the time will be
            recorded against this acceptance.
          </span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', marginTop: 'var(--sp-4)' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowTerms(false)}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={!agreed || saving} onClick={() => setEnabled(true)}>
            {saving ? 'Saving…' : 'Accept and turn on'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

/**
 * PortalSettings — the composite of portal-wide toggles (feature #35).
 * GET/PUT /api/org-settings/portal-settings.
 */
interface EmailTemplateRow {
  slug: string; name: string; scope: string; roles: string[]; where: string;
  subject: string; body_html: string; default_subject: string; default_body_html: string; customized: boolean;
}

/**
 * EmailTemplatesEditor — the Org Settings card for the five client-facing templates (ATA part 4 §4).
 * Pick a template, edit its subject and HTML body, save the override, or reset to the seeded
 * wording. Overrides are stored per slug; anything not overridden keeps the default.
 */
function EmailTemplatesEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const rowsQuery = useQuery<EmailTemplateRow[]>({
    queryKey: ['email-templates'],
    queryFn: ({ signal }) => api.get<EmailTemplateRow[]>('/api/email-templates', signal),
    retry: false,
  });
  const overridesQuery = useQuery<Record<string, { subject?: string; body_html?: string }>>({
    queryKey: ['org-settings', 'email-templates'],
    queryFn: ({ signal }) => api.get('/api/org-settings/email-templates', signal),
    retry: false,
  });
  const [slug, setSlug] = useState('');
  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const rows = rowsQuery.data ?? [];
  const row = rows.find((r) => r.slug === slug) ?? rows[0];
  const currentSubject = subject ?? row?.subject ?? '';
  const currentBody = body ?? row?.body_html ?? '';
  const dirty = !!row && (currentSubject !== row.subject || currentBody !== row.body_html);

  const persist = async (next: Record<string, { subject?: string; body_html?: string }>, message: string) => {
    setSaving(true);
    try {
      await api.put('/api/org-settings/email-templates', { email_templates: next });
      await queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'email-templates'] });
      setSubject(null); setBody(null);
      toast({ variant: 'success', message });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally { setSaving(false); }
  };
  const save = () => {
    if (!row) return;
    const next = { ...(overridesQuery.data ?? {}) };
    const o: { subject?: string; body_html?: string } = {};
    if (currentSubject !== row.default_subject) o.subject = currentSubject;
    if (currentBody !== row.default_body_html) o.body_html = currentBody;
    if (Object.keys(o).length) next[row.slug] = o; else delete next[row.slug];
    persist(next, `${row.name} saved.`);
  };
  const reset = () => {
    if (!row) return;
    const next = { ...(overridesQuery.data ?? {}) };
    delete next[row.slug];
    persist(next, `${row.name} reset to the seeded wording.`);
  };

  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      {rowsQuery.isLoading || !row ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'grid', gap: 'var(--sp-1)', minWidth: 260 }}>
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>Template</span>
              <Select aria-label="Email template" value={row.slug} onChange={(e) => { setSlug(e.target.value); setSubject(null); setBody(null); }}
                options={rows.map((r) => ({ value: r.slug, label: `${r.name}${r.customized ? ' (customized)' : ''}` }))} />
            </label>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', flex: 1 }}>{row.where}. Generated by {row.roles.map((x) => x.replace(/_/g, ' ')).join(', ')}.</span>
            {canEdit && row.customized && <Button variant="ghost" size="sm" onClick={reset} disabled={saving}>Reset to default</Button>}
            {canEdit && dirty && <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>}
          </div>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>Subject</span>
            <Input aria-label="Template subject" value={currentSubject} onChange={(e) => setSubject(e.target.value)} disabled={!canEdit} />
          </label>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>Body (HTML)</span>
            <textarea aria-label="Template body" value={currentBody} onChange={(e) => setBody(e.target.value)} disabled={!canEdit} rows={16}
              style={{ width: '100%', fontFamily: 'var(--ff-mono, monospace)', fontSize: 'var(--fs-xs)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)' }} />
          </label>
        </div>
      )}
    </div>
  );
}

interface PortalSettingsConfig {
  esignature_enabled: boolean;
  allow_document_upload: boolean;
  allow_online_payment: boolean;
  show_appeal_status: boolean;
  // ATA part 3 §6: a documents-only portal, the values glass case, rejections kept internal, no portal MFA.
  allow_comments: boolean;
  allow_notifications: boolean;
  values_glass_case: boolean;
  notify_client_on_rejection: boolean;
  portal_mfa_exempt: boolean;
}

const PORTAL_TOGGLES: { key: keyof PortalSettingsConfig; label: string; description: string }[] = [
  { key: 'esignature_enabled', label: 'E-signature', description: 'Clients can draw and save a reusable signature to sign forms and authorizations.' },
  { key: 'allow_document_upload', label: 'Document uploads', description: 'Clients can upload documents, which land in the staff Document Review Queue.' },
  { key: 'allow_online_payment', label: 'Online payment', description: 'Clients can pay invoices online from the portal.' },
  { key: 'show_appeal_status', label: 'Appeal status', description: 'Clients can see the live status of their appeals.' },
  { key: 'allow_comments', label: 'Comments', description: 'The client and firm comment thread in the portal. Off for a documents-only portal.' },
  { key: 'allow_notifications', label: 'Notifications', description: 'The portal bell and Notifications tab. Off for a documents-only portal.' },
  { key: 'values_glass_case', label: 'Values glass case', description: 'A parcel\'s values reach the portal only after Audit Complete and Share with Client on the value record.' },
  { key: 'notify_client_on_rejection', label: 'Tell the client when an upload is rejected', description: 'On (the default), the client gets a notice with the review note. Off keeps a rejection internal; the Manager follows up by email either way.' },
  { key: 'portal_mfa_exempt', label: 'No second factor for portal users', description: 'One shared login per client company; the firm\'s MFA mandate does not apply to portal users.' },
];

/**
 * PortalSettingsEditor — the Org Settings card for the client-portal toggles.
 * Draft-vs-persisted working copy like the other editors; Save is enabled only
 * when a toggle differs. super_admin only (server-enforced).
 */
function PortalSettingsEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const cfgQuery = useQuery<PortalSettingsConfig>({
    queryKey: ['org-settings', 'portal-settings'],
    queryFn: ({ signal }) => api.get<PortalSettingsConfig>('/api/org-settings/portal-settings', signal),
    retry: false,
  });

  const [draft, setDraft] = useState<PortalSettingsConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const persisted = cfgQuery.data;
  const value = draft ?? persisted ?? null;

  const dirty = draft !== null && persisted != null &&
    PORTAL_TOGGLES.some((t) => draft[t.key] !== persisted[t.key]);

  function toggle(key: keyof PortalSettingsConfig, checked: boolean) {
    setDraft({ ...(value as PortalSettingsConfig), [key]: checked });
  }

  async function save() {
    if (!value) return;
    setSaving(true);
    try {
      await api.put('/api/org-settings/portal-settings', { portal_settings: value });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'portal-settings'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Portal settings saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>Portal features</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>

      {cfgQuery.isLoading || !value ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {PORTAL_TOGGLES.map((t) => (
            <Checkbox
              key={t.key}
              label={t.label}
              description={t.description}
              checked={value[t.key]}
              disabled={!canEdit}
              onChange={(e) => toggle(t.key, e.target.checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Commission role percents (ATA part 3 §3a) ───────────────────────────────

type RolePercents = { consultant: [number, number]; manager: [number, number]; originator: [number, number] };
const PERCENT_ROLE_LABELS: Array<{ key: keyof RolePercents; label: string }> = [
  { key: 'consultant', label: 'Consultant' }, { key: 'manager', label: 'Manager' }, { key: 'originator', label: 'Originator' },
];

/** The fixed percent each slot of a commission role receives; six figures totalling 100. Nobody types a percent anywhere else. */
function CommissionRolePercentsEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const cfgQuery = useQuery<{ commission_role_percents: RolePercents }>({
    queryKey: ['org-settings', 'commission-role-percents'],
    queryFn: ({ signal }) => api.get('/api/org-settings/commission-role-percents', signal),
    retry: false,
  });
  const [draft, setDraft] = useState<RolePercents | null>(null);
  const [saving, setSaving] = useState(false);
  const raw = cfgQuery.data?.commission_role_percents;
  const persisted: RolePercents | null = raw && PERCENT_ROLE_LABELS.every((r) => Array.isArray(raw[r.key]) && raw[r.key].length === 2) ? raw : null;
  const value = draft ?? persisted;
  const total = value ? PERCENT_ROLE_LABELS.reduce((a, r) => a + Number(value[r.key][0] || 0) + Number(value[r.key][1] || 0), 0) : 0;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(persisted);

  function set(key: keyof RolePercents, slot: 0 | 1, raw: string) {
    if (!value) return;
    const next: RolePercents = { consultant: [...value.consultant] as [number, number], manager: [...value.manager] as [number, number], originator: [...value.originator] as [number, number] };
    next[key][slot] = raw === '' ? 0 : Number(raw);
    setDraft(next);
  }
  async function save() {
    if (!value) return;
    setSaving(true);
    try {
      await api.put('/api/org-settings/commission-role-percents', { commission_role_percents: value });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'commission-role-percents'] });
      setDraft(null);
      toast({ variant: 'success', message: 'Commission role percents saved.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally { setSaving(false); }
  }

  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Commission role percents</h3>
        {canEdit && dirty && (
          <Button variant="primary" size="sm" onClick={save} disabled={saving || total !== 100}>{saving ? 'Saving…' : 'Save'}</Button>
        )}
      </div>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
        Each commission role has two slots with a fixed percent. One person alone in a role receives both slots; two people receive them in slot order. The people come from each property's Assignments card.
      </p>
      {!value ? (
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Loading…</span>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 'var(--sp-2) var(--sp-3)', alignItems: 'center', fontSize: 'var(--fs-sm)' }}>
          <span />
          <span style={{ color: 'var(--color-ink-3)' }}>Slot 1</span>
          <span style={{ color: 'var(--color-ink-3)' }}>Slot 2</span>
          {PERCENT_ROLE_LABELS.map((r) => (
            <React.Fragment key={r.key}>
              <span>{r.label}</span>
              {([0, 1] as const).map((slot) => (
                <Input key={slot} type="number" min={0} max={100} aria-label={`${r.label} slot ${slot + 1} percent`} value={String(value[r.key][slot])} disabled={!canEdit} onChange={(e) => set(r.key, slot, e.target.value)} style={{ width: '100%' }} />
              ))}
            </React.Fragment>
          ))}
          <span style={{ fontWeight: 'var(--fw-medium)' }}>Total</span>
          <span data-testid="role-percents-total" style={{ gridColumn: 'span 2', fontWeight: 'var(--fw-medium)', color: total === 100 ? 'var(--color-ink)' : 'var(--color-status-red)' }}>{total}%{total !== 100 ? ' (must total 100%)' : ''}</span>
        </div>
      )}
    </div>
  );
}


// ── Invoicing (ATA part 3 §5a, §5c) ────────────────────────────────────────
interface UserLite { id: string; first_name: string | null; last_name: string | null; email: string; role: string }

/**
 * Three invoicing settings on one card: whether a savings event drafts the property's invoice
 * for the Manager to approve, whether billing-critical fields block a client or property save,
 * and who the invoicing gatekeeper is (sends approved invoices, copied on rejections).
 */
function InvoicingEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const autoDraft = useQuery<{ invoicing_auto_draft: { enabled: boolean } }>({
    queryKey: ['org-settings', 'invoicing-auto-draft'],
    queryFn: ({ signal }) => api.get('/api/org-settings/invoicing-auto-draft', signal), retry: false,
  });
  const required = useQuery<{ billing_required_fields: { enabled: boolean } }>({
    queryKey: ['org-settings', 'billing-required-fields'],
    queryFn: ({ signal }) => api.get('/api/org-settings/billing-required-fields', signal), retry: false,
  });
  const gatekeeper = useQuery<{ invoicing_gatekeeper: { user_id: string | null } }>({
    queryKey: ['org-settings', 'invoicing-gatekeeper'],
    queryFn: ({ signal }) => api.get('/api/org-settings/invoicing-gatekeeper', signal), retry: false,
  });
  const users = useQuery<UserLite[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<UserLite[]>('/api/users-list', signal), retry: false,
  });
  const taskEmails = useQuery<{ task_emails: { enabled: boolean } }>({
    queryKey: ['org-settings', 'task-emails'],
    queryFn: ({ signal }) => api.get('/api/org-settings/task-emails', signal), retry: false,
  });
  const taskEmailsOn = taskEmails.data?.task_emails?.enabled === true;
  const [saving, setSaving] = useState<string | null>(null);

  async function put(slug: string, body: Record<string, unknown>, done: string) {
    setSaving(slug);
    try {
      await api.put(`/api/org-settings/${slug}`, body);
      await queryClient.invalidateQueries({ queryKey: ['org-settings', slug] });
      toast({ variant: 'success', message: done });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally { setSaving(null); }
  }

  const autoOn = autoDraft.data?.invoicing_auto_draft?.enabled === true;
  const requiredOn = required.data?.billing_required_fields?.enabled === true;
  const gatekeeperId = gatekeeper.data?.invoicing_gatekeeper?.user_id ?? '';
  const userList = Array.isArray(users.data) ? users.data : [];

  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', marginTop: 'var(--sp-4)' }}>
      <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Invoicing</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <Checkbox
          label="Draft invoices from savings events"
          description="A recorded savings outcome puts the fee on the property's draft invoice and asks the property's Manager to approve it. Off, fees wait as billable items for the manual Generate invoice step."
          checked={autoOn}
          disabled={!canEdit || saving !== null || !autoDraft.data}
          onChange={(e) => put('invoicing-auto-draft', { invoicing_auto_draft: { enabled: e.target.checked } }, e.target.checked ? 'Savings events now draft invoices.' : 'Savings events no longer draft invoices.')}
        />
        <Checkbox
          label="Email a task summary to the assignee"
          description="Every task assignment also sends the assignee a summary email with the deadline and a link. Off, a task raises only its bell notification."
          checked={taskEmailsOn}
          disabled={!canEdit || saving !== null || !taskEmails.data}
          onChange={(e) => put('task-emails', { task_emails: { enabled: e.target.checked } }, e.target.checked ? 'Task assignments now email the assignee.' : 'Task assignments no longer email.')}
        />
        <Checkbox
          label="Billing fields block save"
          description="A client needs a Manager and an Originator; a property needs a client with both and a fee term for its year. Consultant slot 1 is checked on the property page and in the setup wizard."
          checked={requiredOn}
          disabled={!canEdit || saving !== null || !required.data}
          onChange={(e) => put('billing-required-fields', { billing_required_fields: { enabled: e.target.checked } }, e.target.checked ? 'Billing fields now block save.' : 'Billing fields no longer block save.')}
        />
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', maxWidth: 420 }}>
          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-2)' }}>Invoicing gatekeeper</span>
          <Select
            aria-label="Invoicing gatekeeper"
            value={gatekeeperId}
            disabled={!canEdit || saving !== null || !gatekeeper.data}
            onChange={(e) => put('invoicing-gatekeeper', { invoicing_gatekeeper: { user_id: e.target.value || null } }, e.target.value ? 'Gatekeeper saved.' : 'Gatekeeper cleared.')}
            placeholder="Nobody"
            options={userList.map((u) => ({ value: u.id, label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email }))}
            style={{ width: '100%' }}
          />
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Gets a task to send each approved invoice to the client and is copied on every rejection.</span>
        </label>
      </div>
    </div>
  );
}


// ── Government date verification (ATA part 3 §9) ───────────────────────────
/**
 * One toggle: whether government dates on the calendar are seed data until an admin confirms
 * them each year. Off by default so every other tenant keeps confirmed-as-entered dates.
 */
function GovernmentDatesEditor({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const q = useQuery<{ calendar_government_verification: { enabled: boolean } }>({
    queryKey: ['org-settings', 'calendar-government-verification'],
    queryFn: ({ signal }) => api.get('/api/org-settings/calendar-government-verification', signal), retry: false,
  });
  const [saving, setSaving] = useState(false);
  const on = q.data?.calendar_government_verification?.enabled === true;
  async function set(enabled: boolean) {
    setSaving(true);
    try {
      await api.put('/api/org-settings/calendar-government-verification', { calendar_government_verification: { enabled } });
      await queryClient.invalidateQueries({ queryKey: ['org-settings', 'calendar-government-verification'] });
      toast({ variant: 'success', message: enabled ? 'Government dates now start Unverified each year.' : 'Government dates are confirmed as entered.' });
    } catch (err) {
      toast({ variant: 'error', message: errText(err, 'Save failed.') });
    } finally { setSaving(false); }
  }
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Government dates</h3>
      <Checkbox
        label="Verify government dates each year"
        description="Assessor deadlines, BPP rendition dates and collector due dates start Unverified until an admin confirms them with the jurisdiction; a moved source date resets them; the year roll advances every statutory date one year for re-verification."
        checked={on}
        disabled={!canEdit || saving || !q.data}
        onChange={(e) => set(e.target.checked)}
      />
    </div>
  );
}
